import React, { ReactElement, useState, useEffect } from "react";
import {
    CustomField,
    FormField as ApiFormField,
    FormFieldType, CustomFieldValueInput, AsyncObjectType, FieldTypedReplaceableValue, CustomFieldValue, FormFieldOptionSettings,
} from "../../__generated__/api";
import EditableControl, { FORM_FIELD, ValueTypes } from "../editable-text/EditableText";
import { Option } from "../select/Select";
import { getValue } from "./FieldTypeValueDisplay";
// import Styles from "./Form.module.scss";

interface FormFieldProps {
    onChange?: (ticket: Partial<{customField?: CustomFieldValueInput}>) => void;
    isEditable: boolean;
    formField: Pick<ApiFormField, "type" | "options" | "levels" | "name" | "id"
    | "placeholder" | "asyncObjectType" | "optionsSettings">;
    value?: CustomField;
    isEditing?: boolean;
    shouldFocus?: boolean;
    isRequired?: boolean;
    isFormField?: boolean;
    defaultValue?: FieldTypedReplaceableValue;
    integrationId?: string;
}

export default function FormField({
    onChange, isEditable, formField, value: valueField, isEditing, shouldFocus, isRequired, isFormField,
    defaultValue, integrationId,
}: FormFieldProps): ReactElement {
    const [display, setDisplay] = useState<string | undefined>(() => valueField?.displayValue?.trim());

    useEffect(() => {
        setDisplay(valueField?.displayValue?.trim());
    }, [valueField]);

    const type = formField?.type ?? valueField?.type;
    const value = getValue(valueField?.value as CustomFieldValue, type, defaultValue) as ValueTypes;

    return (
        <EditableControl
            isReadOnly={!isEditable}
            placeholder={formField.placeholder || `Enter ${formField?.name}`}
            type={FORM_FIELD[type as FormFieldType]}
            options={formField?.options as Option[]}
            labels={formField?.levels as string[]}
            value={value}
            integrationId={integrationId}
            optionsSettings={formField.optionsSettings as FormFieldOptionSettings}
            asyncObjectType={formField.asyncObjectType as AsyncObjectType | undefined}
            isEditing={isEditing}
            isRequired={isRequired}
            isFormField={isFormField}
            shouldFocus={shouldFocus ?? true}
            onChange={(changed, replacements, text) => {
                setDisplay(text ?? (Array.isArray(changed) ? changed.join(", ") : changed as string));
                if (!onChange) {
                    return;
                }
                const customValue: CustomFieldValueInput = {
                    formFieldId: formField?.id,
                };
                if (changed != null) {
                    if (type === FormFieldType.MultiSelectDropdown
                        || type === FormFieldType.MultiLevelDropdown
                        || type === FormFieldType.Dropdown
                        || type === FormFieldType.Users
                    ) {
                        customValue.options = changed as string[];
                    } else if (type === FormFieldType.Boolean) {
                        customValue.boolean = changed as boolean;
                    } else if (type === FormFieldType.Time) {
                        customValue.date = new Date(`0000-01-01T${changed}:00Z`);
                    } else if (type === FormFieldType.Date || type === FormFieldType.DateTime) {
                        customValue.date = new Date(changed as string);
                    } else if (type === FormFieldType.Decimal || type === FormFieldType.Number) {
                        customValue.number = parseFloat(changed as string);
                    } else if (type === FormFieldType.Text
                        || type === FormFieldType.LongText
                        || type === FormFieldType.User) {
                        customValue.string = changed as string;
                    } else if (type === FormFieldType.AsyncDropdown) {
                        customValue.displayValue = text as string;
                        customValue.string = changed as string;
                    }
                }

                onChange({
                    customField: customValue,
                });
            }}
        >
            {display ? (
                <span>
                    {display}
                </span>
            ) : undefined}
        </EditableControl>
    );
}

FormField.defaultProps = {
    onChange: () => {
        // nothing
    },
    value: undefined,
    isEditing: false,
    shouldFocus: true,
    isRequired: false,
    isFormField: undefined,
    defaultValue: undefined,
    integrationId: undefined,
};
